import { cn } from '../../lib/cn';
import { Card } from './Card';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import type { ReasoningPresentation } from '../../aix/framework';

type Props = {
  reasoning: ReasoningPresentation;
  confidence?: number;
  className?: string;
  defaultExpanded?: boolean;
};

export function ReasoningPanel({ reasoning, confidence, className, defaultExpanded = true }: Props) {
  return (
    <Card className={cn('border-ai-100 bg-ai-50/30 dark:border-ai-900 dark:bg-ai-950/20', className)}>
      <details open={defaultExpanded}>
        <summary className="cursor-pointer list-none text-sm font-medium text-slate-900 dark:text-slate-100">
          <span className="inline-flex items-center gap-2">
            AI Reasoning
            {reasoning.confidence_level && (
              <span className="rounded-full bg-ai-100 px-2 py-0.5 text-xs font-normal text-ai-700 dark:bg-ai-900 dark:text-ai-300">
                {reasoning.confidence_level}
              </span>
            )}
          </span>
        </summary>
        <div className="mt-3 space-y-3 text-sm">
          <p className="text-slate-700 dark:text-slate-300">{reasoning.summary}</p>
          {confidence != null && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Confidence</span>
              <ConfidenceIndicator score={confidence} size="sm" />
            </div>
          )}
          {reasoning.key_factors && reasoning.key_factors.length > 0 && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Key Factors</p>
              <ul className="mt-1 list-inside list-disc text-slate-600 dark:text-slate-400">
                {reasoning.key_factors.map((f) => <li key={f}>{f}</li>)}
              </ul>
            </div>
          )}
          {reasoning.evidence && reasoning.evidence.length > 0 && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Evidence</p>
              <ul className="mt-1 space-y-0.5 text-xs text-slate-600 dark:text-slate-400">
                {reasoning.evidence.map((e) => <li key={e}>• {e}</li>)}
              </ul>
            </div>
          )}
          {reasoning.tradeoffs && reasoning.tradeoffs.length > 0 && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Trade-offs</p>
              <ul className="mt-1 list-inside list-disc text-slate-600 dark:text-slate-400">
                {reasoning.tradeoffs.map((t) => <li key={t}>{t}</li>)}
              </ul>
            </div>
          )}
          {reasoning.supporting_data && reasoning.supporting_data.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {reasoning.supporting_data.map((d) => (
                <span key={d} className="rounded-md bg-white px-2 py-1 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                  {d}
                </span>
              ))}
            </div>
          )}
        </div>
      </details>
    </Card>
  );
}
